import {useMemo} from 'react';

import {useSelectedCountry} from '../contexts/CountryContext';
import {Country} from '../types/geolocations';
import {FiltersUsers} from '../types/users';

/**
 * Regla compartida de pertenencia a país: cuenta solo el país asignado
 * explícitamente; sin países asignados el usuario no pertenece a ninguno.
 *
 * @param user usuario con sus países asignados
 * @param countryId país contra el que se compara
 * @returns true si el usuario tiene asignado ese país
 */
export function belongsToCountry(
  user: {countries?: Country[]},
  countryId: Country['id'],
): boolean {
  return user.countries?.some(country => country.id === countryId) ?? false;
}

/**
 * Filtra una lista de usuarios por el país seleccionado globalmente
 * (`useSelectedCountry`), leyendo los países de cada usuario (`user.countries`).
 *
 * - Multi-país: un usuario aparece si alguno de sus países es el seleccionado.
 * - Estricto: los usuarios sin país asignado se ocultan cuando hay país.
 * - `enabled = false` desactiva el filtro.
 */
export function useCountryFilteredUsers(
  listUsers: FiltersUsers[],
  enabled = true,
): FiltersUsers[] {
  const {selectedCountry} = useSelectedCountry();

  return useMemo<FiltersUsers[]>(() => {
    if (!enabled || !selectedCountry) {
      return listUsers;
    }
    return listUsers.filter(
      user => !!user.id && belongsToCountry(user, selectedCountry.id),
    );
  }, [enabled, listUsers, selectedCountry]);
}
